// React core and hooks
import React, { useEffect, useMemo, useState } from "react"

// Third-party libraries
import moment from "moment"
import { Helmet } from "react-helmet"
import { Card, Col, Image, Input, notification, Progress, Row, Tag } from "antd"
import {
	DashboardOutlined,
	CalendarOutlined,
	WarningOutlined,
} from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"

// Project utilities and constants
import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import { setBreadcrumb, setLoader } from "store/actions"
import { ALL_CONTRACTS_VALUE } from "hooks"

// Styles
import "./styles.scss"
import { ContentWrapper, GoogleMaps } from "components"
import { getStatusLabel } from "screens/Orders/utils"
import { Duration } from "luxon"

const RouteDetail: React.FC = () => {
	const dispatch = useDispatch()

	// Redux state selectors
	const { business: selectedBusiness, contract: rawSelectedContract } =
		useSelector((state: any) => state.filters) || {}
	// "All contracts" is a Routes-only concept (the filter is shared across
	// screens) — this single-route screen has no single contract to scope by.
	const selectedContract =
		rawSelectedContract === ALL_CONTRACTS_VALUE ? null : rawSelectedContract
	const { routeId } = useParams<{ routeId: string }>()

	// State variables
	const [route, setRoute] = useState<any>({})
	const [markers, setMarkers] = useState<any[]>([])
	const [boundingBox, setBoundingBox] = useState<any>(undefined)
	const mapRef = React.useRef<google.maps.Map | null>(null)

	// Google-routed vans/bikes carry the planned road path as an encoded
	// polyline (Route4me routes have none — nothing to decode there). Follows
	// the actual roads, unlike the straight lines drawn between GPS stops.
	const plannedRoutePath = useMemo(() => {
		if (!route?.googleRoutePolyline || !window.google?.maps?.geometry) {
			return undefined
		}
		return window.google.maps.geometry.encoding
			.decodePath(route.googleRoutePolyline)
			.map((point) => ({ lat: point.lat(), lng: point.lng() }))
	}, [route?.googleRoutePolyline])

	// Get the orders based on filters
	useEffect(() => {
		getRoute(
			dispatch,
			routeId,
			selectedBusiness, // Replace with actual business ID
			selectedContract, // Replace with actual contract ID
			setRoute
		)
	}, [dispatch, routeId, selectedBusiness, selectedContract])

	// Create markers and bounding box for the map
	useEffect(() => {
		if (route.addresses) {
			const markers = route.addresses
				.map((address: any) => {
					const order = route.matchedOrders.find(
						(order: any) => order.optimizerOrderId === address.order_id
					)
					const lat = address.lat
					const lng = address.lng || address.long
					if (
						!lat ||
						!lng ||
						typeof lat !== "number" ||
						typeof lng !== "number"
					)
						return undefined

					let fillColor = "#DB4437" // Default: delivery (not delivered)
					const stopType =
						address.address_stop_type?.toUpperCase() ||
						address.stopType?.toUpperCase()

					if (address.isDepot) {
						fillColor = "#4285F4" // Depot: blue
					} else if (stopType === "PICKUP") {
						fillColor = "#F4B400" // Pickup: yellow/orange
					} else if (stopType === "DELIVERY") {
						// Check delivery status
						const orderStatus = order?.orderFlows?.[0]?.status

						switch (orderStatus) {
							case "delivered":
								fillColor = "green" // Delivered: green
								break
							case "not_delivered":
								fillColor = "#DB4437" // Not delivered: red
								break
							case "cancelled":
								fillColor = "#DB4437" // Cancelled: red
								break
							case "returned":
								fillColor = "#DB4437" // Returned: red
								break
							case "dispute":
								fillColor = "#DB4437" // Dispute: red
								break
							default:
								fillColor = "grey" // Grey for unknown or other statuses
						}
					}

					return {
						lat,
						lng,
						icon: {
							path: 0, // google.maps.SymbolPath.CIRCLE === 0
							fillColor,
							fillOpacity: 1,
							scale: 8,
							strokeColor: "black",
							strokeWeight: 1,
						},
						overlay: <MarkerOverlay address={address} order={order} />,
					}
				})
				.filter((marker: any): marker is NonNullable<typeof marker> => !!marker)

			const boundingBox = markers.length
				? {
						south: Math.min(...markers.map((m: any) => m.lat)),
						west: Math.min(...markers.map((m: any) => m.lng)),
						north: Math.max(...markers.map((m: any) => m.lat)),
						east: Math.max(...markers.map((m: any) => m.lng)),
					}
				: undefined

			// Set the markers and bounding box in state or context if needed
			setMarkers(markers)
			setBoundingBox(boundingBox)
		}
	}, [route])

	// Update map bounds when bounding box changes
	useEffect(() => {
		if (mapRef.current && boundingBox) {
			mapRef.current.fitBounds(boundingBox)
		}
	}, [boundingBox])

	return (
		<div className="ScreenRouteDetail">
			{/* Helmet for SEO and meta tags */}
			{/* TODO: Investigate and check if this is needed for an internal app */}
			<Helmet>
				<title>{Strings.sidebar.orders}</title>
				<meta name="description" content="Check route details" />
			</Helmet>
			<ContentWrapper>
				<Col xs={24}>
					<h2 className="TitleSeparator">
						{Strings.generic.generalInformation}
					</h2>
				</Col>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={16} xl={10}>
						<label htmlFor="routeName" className="InputLabel">
							{Strings.routes.name}
						</label>
						<Input
							id="routeName"
							value={route.name || "-"}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={8} xl={5}>
						<label htmlFor="routeType" className="InputLabel">
							{Strings.routes.type}
						</label>
						<Input
							id="routeType"
							value={
								route.routeType
									? Strings.routes.types[
											route.routeType as keyof typeof Strings.routes.types
										]
									: route.routeType || "-"
							}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={5} xl={3}>
						<label htmlFor="routeDate" className="InputLabel">
							{Strings.orders.routeDate}
						</label>
						<Input
							id="routeDate"
							value={
								route.date ? moment.utc(route.date).format("DD-MM-YYYY") : ""
							}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={5} xl={3}>
						<label htmlFor="startTime" className="InputLabel">
							{Strings.routes.startTime}
						</label>
						<Input
							id="startTime"
							value={
								route.startedRouteDate
									? new Date(route.startedRouteDate).toLocaleTimeString(
											"en-US",
											{
												hour: "2-digit",
												minute: "2-digit",
												hour12: false,
											}
										)
									: "-"
							}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={5} xl={3}>
						<label htmlFor="endTime" className="InputLabel">
							{Strings.routes.endTime}
						</label>
						<Input
							id="endTime"
							value={
								route.endedRouteDate
									? new Date(route.endedRouteDate).toLocaleTimeString("en-US", {
											hour: "2-digit",
											minute: "2-digit",
											hour12: false,
										})
									: "-"
							}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={9} xl={6}>
						<label htmlFor="status" className="InputLabel">
							{Strings.orders.status}
						</label>
						<Input
							id="routeType"
							value={getStatusLabel(route.status)}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={5}>
						<label htmlFor="numberOfDeliveries" className="InputLabel">
							{Strings.routes.numberOfDeliveries}
						</label>
						<Input
							id="numberOfDeliveries"
							value={
								route.addresses
									? route.addresses.filter(
											(val: any) =>
												val.address_stop_type?.toUpperCase() === "DELIVERY" ||
												val.stopType?.toUpperCase() === "DELIVERY"
										).length
									: 0
							}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={5}>
						<label htmlFor="numberOfPickups" className="InputLabel">
							{Strings.routes.numberOfPickups}
						</label>
						<Input
							id="numberOfPickups"
							value={
								route.addresses
									? route.addresses.filter(
											(val: any) =>
												val.address_stop_type?.toUpperCase() === "PICKUP" ||
												val.stopType?.toUpperCase() === "PICKUP"
										).length
									: 0
							}
							readOnly
							placeholder={Strings.routes.numberOfPickups}
							className="OrderInputReadOnly"
						/>
					</Col>
					<Col xs={24} md={14} xl={8}>
						<label htmlFor="route4meId" className="InputLabel">
							Route4me ID
						</label>
						<Input
							id="route4meId"
							value={route.route4meRouteId || "-"}
							readOnly
							className="OrderInputReadOnly"
						/>
					</Col>
				</Row>
				<div className="mt-4 mb-4">
					<GoogleMaps
						zoom={15}
						boundingBox={boundingBox}
						mapContainerStyle={{
							width: "100%",
							height: "60vh",
							borderRadius: 5,
						}}
						options={{
							panControl: false,
						}}
						markers={markers}
						polylines={
							plannedRoutePath?.length
								? [
										{
											path: plannedRoutePath,
											strokeColor: "#1890ff",
											strokeOpacity: 0.6,
											strokeWeight: 4,
											showDirection: true,
										},
									]
								: []
						}
						mapRef={mapRef}
					/>
				</div>
			</ContentWrapper>
			{route.vehicleCheckIn && (
				<ContentWrapper>
					<VehicleCheckInSection checkIn={route.vehicleCheckIn} />
				</ContentWrapper>
			)}
		</div>
	)
}

const BooleanTag: React.FC<{ value: boolean | null | undefined }> = ({
	value,
}) => {
	if (value == null) return <span>-</span>
	return (
		<Tag color={value ? "green" : "red"}>
			{value ? Strings.generic.yes : Strings.generic.no}
		</Tag>
	)
}

const VehicleCheckInSection: React.FC<{ checkIn: any }> = ({ checkIn }) => {
	const defects = checkIn.defects
	const attachments = defects?.attachments || []
	const strings = Strings.routes.vehicleCheckIn

	const fuelColor =
		checkIn.fuelPercentage > 50
			? "#52c41a"
			: checkIn.fuelPercentage > 20
				? "#faad14"
				: "#ff4d4f"

	return (
		<>
			<Col xs={24}>
				<h2 className="TitleSeparator">{strings.title}</h2>
			</Col>

			<Row gutter={[16, 16]}>
				<Col xs={24} md={8}>
					<Card className="VehicleCheckIn__statCard" bordered={false}>
						<DashboardOutlined className="VehicleCheckIn__statIcon" />
						<div className="VehicleCheckIn__statValue">
							{checkIn.odometerKm != null
								? checkIn.odometerKm.toLocaleString()
								: "-"}
						</div>
						<div className="VehicleCheckIn__statLabel">{strings.odometer}</div>
					</Card>
				</Col>
				<Col xs={24} md={8}>
					<Card className="VehicleCheckIn__statCard" bordered={false}>
						<Progress
							type="circle"
							percent={checkIn.fuelPercentage ?? 0}
							width={64}
							strokeColor={fuelColor}
							format={(pct) =>
								checkIn.fuelPercentage != null ? `${pct}%` : "-"
							}
						/>
						<div
							className="VehicleCheckIn__statLabel"
							style={{ marginTop: 12 }}
						>
							{strings.fuel}
						</div>
					</Card>
				</Col>
				<Col xs={24} md={8}>
					<Card className="VehicleCheckIn__statCard" bordered={false}>
						<CalendarOutlined className="VehicleCheckIn__statIcon" />
						<div className="VehicleCheckIn__statValue">
							{checkIn.inspectionTimestamp
								? moment(checkIn.inspectionTimestamp).format("HH:mm")
								: ""}
						</div>
						<div className="VehicleCheckIn__statSubValue">
							{checkIn.inspectionTimestamp
								? moment(checkIn.inspectionTimestamp).format("DD-MM-YYYY")
								: "-"}
						</div>
						<div className="VehicleCheckIn__statLabel">
							{strings.inspectionDate}
						</div>
					</Card>
				</Col>
			</Row>

			<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
				<Col xs={24} md={8}>
					<div className="VehicleCheckIn__checkItem">
						<span className="VehicleCheckIn__checkLabel">
							{strings.tirePressure}
						</span>
						<BooleanTag value={checkIn.tirePressureOk} />
					</div>
				</Col>
				<Col xs={24} md={8}>
					<div className="VehicleCheckIn__checkItem">
						<span className="VehicleCheckIn__checkLabel">
							{strings.lightsWorking}
						</span>
						<BooleanTag value={checkIn.lightsWorking} />
					</div>
				</Col>
				<Col xs={24} md={8}>
					<div className="VehicleCheckIn__checkItem">
						<span className="VehicleCheckIn__checkLabel">
							{strings.hasDefects}
						</span>
						{defects?.hasDefects ? (
							<Tag icon={<WarningOutlined />} color="error">
								{Strings.generic.yes}
							</Tag>
						) : (
							<Tag color="success">{Strings.generic.no}</Tag>
						)}
					</div>
				</Col>
			</Row>

			{(checkIn.generalNotes || defects?.defectNotes) && (
				<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
					{checkIn.generalNotes && (
						<Col xs={24} md={defects?.defectNotes ? 12 : 24}>
							<div className="VehicleCheckIn__noteCard">
								<div className="VehicleCheckIn__noteHeader">
									{strings.generalNotes}
								</div>
								<div className="VehicleCheckIn__noteBody">
									{checkIn.generalNotes}
								</div>
							</div>
						</Col>
					)}
					{defects?.defectNotes && (
						<Col xs={24} md={checkIn.generalNotes ? 12 : 24}>
							<div className="VehicleCheckIn__noteCard VehicleCheckIn__noteCard--defect">
								<div className="VehicleCheckIn__noteHeader">
									<WarningOutlined style={{ marginRight: 6 }} />
									{strings.defectNotes}
								</div>
								<div className="VehicleCheckIn__noteBody">
									{defects.defectNotes}
								</div>
							</div>
						</Col>
					)}
				</Row>
			)}

			{attachments.length > 0 && (
				<div style={{ marginTop: 16 }}>
					<label className="InputLabel">
						{strings.defectPhotos} ({attachments.length})
					</label>
					<Image.PreviewGroup>
						<div className="ImageGalleryImageContainer">
							{attachments.map((att: any, idx: number) => (
								<div className="ImageWrapper" key={att._id?.$oid || idx}>
									<Image
										width={200}
										height={200}
										src={att.url}
										style={{ objectFit: "cover", borderRadius: 8 }}
									/>
								</div>
							))}
						</div>
					</Image.PreviewGroup>
				</div>
			)}
		</>
	)
}

const MarkerOverlay: React.FC<{ address: any; order?: any }> = ({
	address,
	order,
}) => {
	const stopType =
		address.address_stop_type?.toUpperCase() || address.stopType?.toUpperCase()

	if (address.isDepot) {
		return DepotOverlayContent({ address }) // Render depot overlay content
	} else if (stopType === "PICKUP") {
		return PickupOverlayContent({ address }) // Render pickup overlay content
	} else if (stopType === "DELIVERY" && !!order) {
		return DeliveriesOverlayContent({ order }) // Render the overlay content with the order details
	} else {
		// Return error or fallback content
		return (
			<div style={{ padding: 8, background: "#fff", borderRadius: 4 }}>
				<div style={{ fontWeight: 600, fontSize: 15 }}>
					{address.address_stop_type || address.stopType || "Unknown Type"}
				</div>
				<div style={{ color: "#aaa" }}>
					{address.address || address.formattedAddress?.address || "No Address"}
				</div>
			</div>
		)
	}
}

const DepotOverlayContent: React.FC<{ address: any }> = ({ address }) => {
	return (
		<div
			style={{
				padding: 8,
				background: "#fff",
				borderRadius: 4,
				minWidth: 200,
			}}
		>
			<div style={{ fontWeight: 600, fontSize: 15 }}>Depot</div>
			<div style={{ color: "#aaa" }}>
				{address.address || address.formattedAddress?.address || "No Address"}
			</div>
		</div>
	)
}

const PickupOverlayContent: React.FC<{ address: any }> = ({ address }) => {
	return (
		<div
			style={{
				padding: 8,
				background: "#fff",
				borderRadius: 4,
				minWidth: 200,
			}}
		>
			<div style={{ fontWeight: 600, fontSize: 15 }}>Pickup</div>
			<div style={{ color: "#aaa" }}>
				{address.address || address.formattedAddress?.address || "No Address"}
			</div>
		</div>
	)
}

const DeliveriesOverlayContent: React.FC<{ order: any }> = ({ order }) => {
	const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null)

	const orderStatus = order.orderFlows[0]?.status
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 4,
				minWidth: 220,
			}}
		>
			<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
				Order ID: <span style={{ color: "#1C70D9" }}>{order.orderId}</span>
				<button
					style={{
						marginLeft: 8,
						padding: "2px 8px",
						fontSize: 12,
						borderRadius: 3,
						border: "1px solid #ddd",
						background: "#f5f5f5",
						cursor: "pointer",
						position: "relative",
					}}
					onClick={async (e) => {
						e.stopPropagation()
						await navigator.clipboard.writeText(order.orderId)
						setCopiedOrderId(order.orderId)
						setTimeout(() => setCopiedOrderId(null), 3000)
					}}
					title="Copy Order ID"
				>
					{copiedOrderId === order.orderId ? "Copied!" : "Copy"}
				</button>
			</div>
			<div>
				<span style={{ fontWeight: 500 }}>Status:</span>{" "}
				<span
					style={{
						color:
							orderStatus === "delivered"
								? "green"
								: orderStatus === "not_delivered" ||
									  orderStatus === "cancelled" ||
									  orderStatus === "returned" ||
									  orderStatus === "dispute"
									? "red"
									: "black",
					}}
				>
					{getStatusLabel(orderStatus)}
				</span>
			</div>
			<div>
				<span style={{ fontWeight: 500 }}>Date:</span>{" "}
				{order?.orderFlows[0]?.deliveryDate ? (
					new Date(order.orderFlows[0].deliveryDate).toLocaleDateString()
				) : (
					<span style={{ color: "#aaa" }}>N/A</span>
				)}
			</div>
			<div>
				<span style={{ fontWeight: 500 }}>Slot:</span>{" "}
				{order?.slot ? (
					`${Duration.fromMillis(order?.slot.from).toFormat(
						"hh:mm"
					)} - ${Duration.fromMillis(order?.slot.to).toFormat("hh:mm")}`
				) : (
					<span style={{ color: "#aaa" }}>N/A</span>
				)}
			</div>
			<div>
				<span style={{ fontWeight: 500 }}>Client:</span>{" "}
				{order.user?.name || <span style={{ color: "#aaa" }}>N/A</span>}
			</div>
			<div>
				<span style={{ fontWeight: 500 }}>Address:</span>{" "}
				{order.formattedAddress?.address || (
					<span style={{ color: "#aaa" }}>N/A</span>
				)}
			</div>
		</div>
	)
}

async function getRoute(
	dispatch: any,
	id: string,
	business: string,
	contract: string,
	setRoute: (route: any) => void
) {
	if (!id) return

	dispatch(setLoader(true))
	try {
		const response = await API.get({
			url: `${Endpoints.uriRoutes(id)}?includeOrders=true`,
			headers: {
				business,
				contract,
			},
		})
		if (response.ok) {
			const { routes } = response.data.results || {}
			setRoute(routes[0])
			dispatch(setBreadcrumb(routes[0].name || ""))
		} else {
			notification.error({
				message: "Strings.sidebar.routes",
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}
	dispatch(setLoader(false))
}

export default RouteDetail
